import React, { useEffect } from "react"
import {
    Box,
    Button,
    IconButton,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Tooltip,
    Typography,
} from "@material-ui/core"
import { makeStyles } from "@material-ui/core/styles"
import { CardGiftcard, Edit, Group, PersonAdd } from "@material-ui/icons"
import { useSnackbar } from "../../../utils/notificacoes"
import { useHistory } from "react-router-dom"
import { useBrindesFornecedorContext } from "../context/useBrindesFornecedorContext"

const useStyles = makeStyles(theme => ({
    container: {
        paddingLeft: "90px",
        paddingRight: "10px",
        paddingTop: "100px",
        paddingBottom: "30px",
        [theme.breakpoints.down("md")]: { paddingLeft: "20px", paddingRight: "20px", paddingTop: "80px" },
        [theme.breakpoints.down("sm")]: { paddingLeft: "15px", paddingRight: "15px", paddingTop: "70px" },
    },
    content: {
        margin: "auto",
        maxWidth: "1000px",
        [theme.breakpoints.down("sm")]: { maxWidth: "100%" },
    },
    titleText: {
        fontWeight: 600,
        color: "#2c3e50",
        [theme.breakpoints.down("sm")]: { fontSize: "1.5rem" },
        [theme.breakpoints.down("xs")]: { fontSize: "1.25rem" },
    },
    subtitle: {
        color: "#7f8c8d",
        marginBottom: theme.spacing(3),
    },
    acoesRow: {
        display: "flex",
        gap: theme.spacing(2),
        flexWrap: "wrap",
        marginBottom: theme.spacing(3),
    },
    btnNovo: {
        borderRadius: 25,
        backgroundColor: "var(--cor-botao-salvar)",
        color: "white",
        fontWeight: 600,
        paddingLeft: theme.spacing(3),
        paddingRight: theme.spacing(3),
        boxShadow: "0 4px 12px rgba(var(--cor-botao-salvar-rgb),0.3)",
        "&:hover": { backgroundColor: "var(--cor-botao-salvar-hover)" },
    },
    btnNovoSecundario: {
        borderRadius: 25,
        fontWeight: 600,
        paddingLeft: theme.spacing(3),
        paddingRight: theme.spacing(3),
    },
    configCard: {
        borderRadius: 20,
        boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
        backgroundColor: "white",
        overflow: "hidden",
        marginBottom: theme.spacing(3),
        [theme.breakpoints.down("sm")]: { borderRadius: 15, marginBottom: theme.spacing(2) },
    },
    cardHeader: {
        backgroundColor: "#f8f9fa",
        padding: theme.spacing(2.5),
        borderBottom: "2px solid #e9ecef",
        display: "flex",
        alignItems: "center",
        [theme.breakpoints.down("sm")]: { padding: theme.spacing(2) },
    },
    cardHeaderTitle: {
        fontWeight: 600,
        color: "#2c3e50",
    },
    iconHeader: {
        color: "#4caf50",
        marginRight: theme.spacing(1),
    },
    cardContent: {
        padding: theme.spacing(3),
        [theme.breakpoints.down("sm")]: { padding: theme.spacing(2) },
    },
    tableHeadCell: {
        fontWeight: 600,
        color: "#2c3e50",
        backgroundColor: "#f8f9fa",
    },
    nomeCell: {
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
    },
    totalRegistros: {
        color: "#7f8c8d",
        marginTop: theme.spacing(2),
        fontSize: "0.9rem",
    },
}))

export function BrindesLista() {
    const classes = useStyles()
    const { dados, erroCarregamento } = useBrindesFornecedorContext()
    const { enqueueSnackbar } = useSnackbar()
    const history = useHistory()

    const alertStart = (msg: string, tipo: "success" | "error" | "info") => {
        enqueueSnackbar(msg, { variant: tipo })
    }

    useEffect(() => {
        if (erroCarregamento) alertStart(erroCarregamento, "error")
    }, [erroCarregamento])

    return (
        <Box className={classes.container}>
            <Box className={classes.content}>
                <Typography variant="h4" className={classes.titleText} gutterBottom>
                    Promoções
                </Typography>
                <Typography variant="body1" className={classes.subtitle}>
                    Aqui você pode configurar promoções para seu estabelecimento.
                </Typography>

                <Box className={classes.acoesRow}>
                    <Button
                        variant="contained"
                        className={classes.btnNovo}
                        startIcon={<CardGiftcard />}
                        onClick={() => history.push("/cadastro-brinde-geral")}
                    >
                        Nova Promoção para Todos os Clientes
                    </Button>
                    <Button
                        variant="outlined"
                        color="primary"
                        className={classes.btnNovoSecundario}
                        startIcon={<PersonAdd />}
                        onClick={() => history.push("/clientes")}
                    >
                        Nova Promoção para Clientes Selecionados
                    </Button>
                </Box>

                <Paper className={classes.configCard}>
                    <Box className={classes.cardHeader}>
                        <CardGiftcard className={classes.iconHeader} />
                        <Typography variant="h6" className={classes.cardHeaderTitle}>
                            Promoções Cadastradas
                        </Typography>
                    </Box>
                    <Box className={classes.cardContent}>
                        <TableContainer style={{ maxHeight: 480, overflowX: "auto" }}>
                            <Table
                                size="small"
                                stickyHeader
                                style={{ minWidth: 840, width: "100%" }}
                            >
                                <TableHead>
                                    <TableRow>
                                        <TableCell className={classes.tableHeadCell} style={{ minWidth: 160 }}>
                                            Título
                                        </TableCell>
                                        <TableCell className={classes.tableHeadCell} style={{ minWidth: 200 }}>
                                            Descrição
                                        </TableCell>
                                        <TableCell className={classes.tableHeadCell} style={{ minWidth: 110 }}>
                                            Início
                                        </TableCell>
                                        <TableCell className={classes.tableHeadCell} style={{ minWidth: 110 }}>
                                            Fim
                                        </TableCell>
                                        <TableCell className={classes.tableHeadCell} style={{ minWidth: 130 }}>
                                            Tipo
                                        </TableCell>
                                        <TableCell className={classes.tableHeadCell} style={{ minWidth: 90 }}>
                                            Destaque
                                        </TableCell>
                                        <TableCell
                                            className={classes.tableHeadCell}
                                            style={{ minWidth: 80 }}
                                            align="center"
                                        />
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {dados.brindes.length === 0 ? (
                                        <TableRow>
                                            <TableCell colSpan={7} align="center">
                                                <Typography
                                                    variant="body2"
                                                    style={{ color: "#7f8c8d", padding: "16px 0" }}
                                                >
                                                    Seu estabelecimento ainda não possui promoções cadastradas.
                                                </Typography>
                                            </TableCell>
                                        </TableRow>
                                    ) : (
                                        dados.brindes.map(brinde => (
                                            <TableRow key={brinde.id} hover>
                                                <TableCell
                                                    className={classes.nomeCell}
                                                    title={brinde.titulo ?? ""}
                                                >
                                                    {brinde.titulo}
                                                </TableCell>
                                                <TableCell>{brinde.descricao}</TableCell>
                                                <TableCell>{brinde.dataInicio ?? "—"}</TableCell>
                                                <TableCell>{brinde.dataFim ?? "—"}</TableCell>
                                                <TableCell
                                                    className={classes.nomeCell}
                                                    title={brinde.tipoTexto}
                                                >
                                                    {brinde.tipoTexto}
                                                </TableCell>
                                                <TableCell>
                                                    {brinde.promocao ? "Sim" : "Não"}
                                                </TableCell>
                                                <TableCell
                                                    align="center"
                                                    style={{ whiteSpace: "nowrap" }}
                                                >
                                                    <Tooltip title="Editar">
                                                        <IconButton
                                                            size="small"
                                                            onClick={() =>
                                                                history.push(
                                                                    `/cadastro-brinde-geral?id=${brinde.id}`
                                                                )
                                                            }
                                                        >
                                                            <Edit fontSize="small" />
                                                        </IconButton>
                                                    </Tooltip>
                                                    {(brinde.numTipo === 0 || brinde.numTipo === 1) && (
                                                        <Tooltip title="Ver Clientes">
                                                            <IconButton
                                                                size="small"
                                                                onClick={() =>
                                                                    history.push(
                                                                        `/ver-clientes-brinde?id=${brinde.id}`
                                                                    )
                                                                }
                                                            >
                                                                <Group fontSize="small" />
                                                            </IconButton>
                                                        </Tooltip>
                                                    )}
                                                </TableCell>
                                            </TableRow>
                                        ))
                                    )}
                                </TableBody>
                            </Table>
                        </TableContainer>

                        <Typography className={classes.totalRegistros}>
                            Exibindo {dados.brindes.length} promoção(ões).
                        </Typography>
                    </Box>
                </Paper>
            </Box>
        </Box>
    )
}
